/**
 * Pure decisions for the foliar session detail screen (F3).
 *
 * No React, no network, no i18n — the page above this file is presentation.
 * Mirrors soilSessionDetail.ts (U3) and foliarRosterSummary.ts (F2).
 *
 * WHAT IS DELIBERATELY PRESENT AND SOIL'S EQUIVALENT LACKS: a real
 * planned-vs-applied comparison, AT SESSION LEVEL. Soil is plan-as-actual by
 * decision (parent spec §2.7 — no operator entry exists), so a variance column
 * there would always read zero. Foliar genuinely captures actuals: each combo
 * books `its own tankCount x its own dosePerTank` (#506 D1/D2), and the tank
 * count is what the operator observed, so the session's applied total CAN
 * differ from its plan and the difference is real.
 *
 * WHAT IS DELIBERATELY ABSENT: a PER-COMBO variance (R15 — see
 * `buildAppliedLines`), and a 'danger' variance tier. A variance is
 * information; nothing about it blocks sign-off.
 */
import type {
  FoliarSessionDetail, FoliarDetailCombo, FoliarAppliedProduct,
} from '../../services/nutritionService';
import { WaitingTone, waitingDaysSince, waitingTone } from './signOffAgeing';

/**
 * Deviation at which a variance stops being a number and becomes a coloured
 * marker (parent spec §10 open item 6, settled here).
 *
 * 5 %, and a named constant so a later slice can lift it to configuration
 * without touching the page. A session whose operators between them applied
 * the planned tank fractions produces applied === planned exactly, so a marker
 * on every row would be noise on nearly every row; the marker exists for the
 * case §2.8 was written for — tank counts that did not add up to the plan.
 */
export const VARIANCE_MARKER_PCT = 5;

/** Guards against 0.30000000000000004 reading as a 0.0000001 % variance. */
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export interface VarianceLine {
  productId: string;
  productName: string;
  planned: number | null;
  applied: number;
  unit: string | null;
  /** null when planned is null or 0 — never Infinity, never NaN. */
  deltaPct: number | null;
  tone: 'warn' | null;
}

/**
 * What ONE combo applied, per product. Deliberately carries NO planned figure
 * and no variance — see `buildAppliedLines`.
 */
export interface AppliedLine {
  productId: string;
  productName: string;
  applied: number;
  unit: string | null;
}

export interface ComboRow extends FoliarDetailCombo {
  applied: AppliedLine[];
}

export function varianceTone(deltaPct: number | null): 'warn' | null {
  if (deltaPct === null) return null;
  return Math.abs(deltaPct) >= VARIANCE_MARKER_PCT ? 'warn' : null;
}

function lineFor(
  productId: string, productName: string, unit: string | null,
  planned: number | null, applied: number,
): VarianceLine {
  // planned === 0 yields Infinity, and planned === null yields NaN. Both
  // render as garbage and sort unpredictably, so the delta is simply unknown
  // and the marker is suppressed rather than invented.
  const deltaPct = planned === null || planned === 0
    ? null
    : round2(((applied - planned) / planned) * 100);
  return {
    productId, productName, unit, planned, applied, deltaPct, tone: varianceTone(deltaPct),
  };
}

/**
 * What ONE combo applied — quantities only, NO planned comparison (R15).
 *
 * A combo's `plannedQuantity` is the WHOLE SESSION's plan, deliberately (spec
 * §6.2: `recomputeParentStatus` COPIES that field onto the parent rather than
 * summing it, so a per-combo share there would make the parent report one
 * operator's share as the whole session's plan). Comparing a combo's portion
 * against it therefore reports a large negative variance for work done exactly
 * to plan — the spec's own worked example, two combos at 0.4 and 0.27 tanks
 * against a 40 kg plan, read -40 % and -59.5 %.
 *
 * The honest per-combo basis under R15 is that combo's own share,
 * `plannedTanksExact x dosePerTank`. **`dosePerTank` is not on this page's
 * wire contract** — `plannedPerTankMix` lives on NutritionOperatorSession and
 * `buildFoliarSessionDetail` (backend/src/services/foliarSessionDetailService.js:79-111)
 * does not project it; only `plannedTankCount`/`plannedTanksExact` come
 * across. Rather than invent a substitute — dividing the applied quantity by
 * `tanksApplied` to recover `dosePerTank` would make the line a restatement of
 * the tank count, which the combos table already shows — the per-combo product
 * variance is SUPPRESSED. If a later slice wants it, put `dosePerTank` on the
 * wire; do not re-derive it here.
 *
 * The session-level comparison below is unaffected: it reads the plan from the
 * session, where the plan actually lives.
 */
export function buildAppliedLines(products: FoliarAppliedProduct[]): AppliedLine[] {
  return (products || []).map((p) => ({
    productId: p.productId,
    productName: p.productName,
    applied: p.quantity,
    unit: p.quantityUnit,
  }));
}

export function buildComboRows(detail: FoliarSessionDetail): ComboRow[] {
  // Spread into a NEW object — the caller's detail must not gain an `applied`
  // field, or a second call would compound it.
  return (detail.combos || []).map((c) => ({ ...c, applied: buildAppliedLines(c.productsApplied) }));
}

/**
 * Session-level planned vs applied (R15).
 *
 * APPLIED is the per-product sum across every combo: since #506 each combo
 * books its OWN portion, so the portions sum to the session's applied total.
 *
 * PLANNED comes from `session.productTotals` — the session's plan, where the
 * plan lives. It is deliberately NOT summed out of the combos' snapshots:
 * every combo carries the WHOLE session's `plannedQuantity` (spec §6.2), so
 * summing it multiplied the plan by the combo count and the table reported
 * planned 80 / applied 40.2 / -49.75 % for two operators who between them did
 * exactly the plan. Before #506 the applied half was doubled too (80 vs 80 =
 * 0 %), which is why the wrong planned figure only surfaced once `applied` was
 * corrected.
 *
 * A product that was applied but is absent from the plan gets `null` — not 0,
 * which would divide to Infinity — and its marker is suppressed. Rows are
 * driven off what was APPLIED, so a planned product nobody has touched yet
 * does not appear; the plan itself is rendered by the mix card above.
 */
export function sessionVariance(detail: FoliarSessionDetail): VarianceLine[] {
  const plannedById = new Map(
    ((detail.session && detail.session.productTotals) || []).map((p) => [p.productId, p]),
  );
  const acc = new Map<string, { name: string; unit: string | null; applied: number }>();
  (detail.combos || []).forEach((c) => {
    (c.productsApplied || []).forEach((p) => {
      const cur = acc.get(p.productId)
        || { name: p.productName, unit: p.quantityUnit, applied: 0 };
      cur.applied += p.quantity || 0;
      acc.set(p.productId, cur);
    });
  });
  return Array.from(acc.entries()).map(([productId, v]) => {
    const plan = plannedById.get(productId);
    return lineFor(
      productId, v.name, v.unit ?? (plan ? plan.unit : null),
      plan && typeof plan.totalQty === 'number' ? plan.totalQty : null,
      round2(v.applied),
    );
  });
}

/**
 * A combo's own planned tank count (#461/D5): `session.tanksPlanned` /
 * `session.tanksExact` no longer exist on the wire — a single session-level
 * scalar cannot describe two combos on two different machines, so each
 * combo carries its own figure instead, and the page shows it against the
 * machine it belongs to. Null when this combo has no planned figure yet
 * (no machine assigned, or assigned before a plan existed to derive from).
 */
export function comboPlannedTanks(c: FoliarDetailCombo): { count: number; exact: number | null } | null {
  if (c.plannedTankCount == null) return null;
  return { count: c.plannedTankCount, exact: c.plannedTanksExact };
}

/**
 * Total tanks applied across combos. Null — never 0 — when nothing has been
 * captured, because "no capture" and "zero tanks" are different facts. A
 * genuine captured 0 survives as 0, which is why the filter tests for `null`
 * rather than using a falsy check.
 */
export function tanksAppliedTotal(detail: FoliarSessionDetail): number | null {
  const captured = (detail.combos || []).filter((c) => c.tanksApplied !== null);
  if (captured.length === 0) return null;
  return round2(captured.reduce((sum, c) => sum + (c.tanksApplied as number), 0));
}

/**
 * The instruction sheet's 409 (NO_MACHINE_ASSIGNED) fires when the session has
 * no PRINTABLE combo — `resolveInstructionCombos`
 * (backend/src/services/nutritionInstructionService.js) classifies every combo
 * and throws when the printable bucket is empty. A mixing sheet is for work
 * still to be done, so an all-completed or all-cancelled session raises it
 * too, and that is the terminal state of every foliar session.
 *
 * This predicate mirrors the LIVE-STATUS half of that rule only. The server
 * also requires a `plannedPerTankMix` on the combo, and that field is not on
 * this page's wire contract (`buildFoliarSessionDetail` does not project it),
 * so the button is still enabled — and the request still 409s — when the
 * session has live combos but NONE of them has a stored plan. A single
 * planless combo does not 409: it is named on the sheet as un-printable while
 * its planned siblings print. The catch in `handlePrintInstructions` remains
 * the backstop; this narrows the common case rather than preventing the error.
 *
 * A combo with a null machineId is deliberately still printable here — the
 * server prints it as an un-printable warning beside its siblings rather than
 * refusing the sheet, so predicating on tankVolumeL would disable a button the
 * server would have served.
 */
const PRINTABLE_COMBO_STATUSES = ['not_started', 'in_progress'];
export function canPrintInstruction(detail: FoliarSessionDetail): boolean {
  return (detail.combos || []).some((c) => PRINTABLE_COMBO_STATUSES.indexOf(c.status) !== -1);
}

/**
 * Delete affordance. Covers 6 of the 8 NutritionApplicationSession.status
 * values: the server now reverses stock for any session that consumed, and
 * demands `confirm: true` before doing so (#485). The previous list omitted
 * 'incomplete' and 'signed_off' because deleting them stranded their ledger
 * rows — that is fixed, so the omission would now be a UI restriction
 * standing in for a server rule that no longer exists.
 *
 * The remaining two omissions are deliberate, not oversights: 'cancelled' has
 * no consumption to strand (a cancelled combo never captured actuals), and
 * 'deleted' is already the delete affordance's own terminal state.
 */
const DELETABLE = ['draft', 'planned', 'in_progress', 'completed', 'incomplete', 'signed_off'];
export function canDeleteSession(status: string): boolean {
  return DELETABLE.indexOf(status) !== -1;
}

/**
 * Cancel affordance (#476): a combo can be stood down only while it is still
 * "live" — not_started or in_progress. The other three combo statuses
 * (completed, incomplete, cancelled) are terminal, and the server 409s on
 * either an already-terminal combo or one that has captured actuals
 * (HAS_ACTUALS), so the affordance must not render at all rather than let
 * the request round-trip to a predictable failure.
 *
 * `CANCELLABLE_COMBO_STATUSES` is the exact complement, within
 * NutritionOperatorSession's 5-value status enum, of the backend's
 * `TERMINAL_COMBO_STATUSES` (backend/src/services/nutritionOperatorSessionService.js).
 * The two lists must change together — if the backend's terminal set ever
 * grows or shrinks, this one has to move with it, or the affordance renders
 * for a status the server will refuse.
 */
const CANCELLABLE_COMBO_STATUSES = ['not_started', 'in_progress'];
export function canCancelCombo(status: string): boolean {
  return CANCELLABLE_COMBO_STATUSES.indexOf(status) !== -1;
}

/**
 * Record affordance (#482): a combo can be retrospectively captured only while
 * it is still "live" — not_started or in_progress. This is the foliar sibling
 * of soilSessionDetail.actionForStatus's 'record' rule.
 *
 * `RECORDABLE_COMBO_STATUSES` must equal the backend's
 * `FOLIAR_ELIGIBLE_STATUSES` (backend/src/services/nutritionBackCaptureService.js),
 * which is the status filter completeFoliar's lookup uses. If the two drift,
 * this page offers a button whose POST the server answers with a 404.
 *
 * Note this is the same pair as CANCELLABLE_COMBO_STATUSES above, and
 * deliberately a separate constant: they answer different questions and one
 * may change without the other.
 */
const RECORDABLE_COMBO_STATUSES = ['not_started', 'in_progress'];
export function canRecordCombo(status: string): boolean {
  return RECORDABLE_COMBO_STATUSES.indexOf(status) !== -1;
}

/**
 * How long this session has been waiting for sign-off, using the SHARED ageing
 * rules (./signOffAgeing) so the detail screen and the queue can never disagree
 * about a row's age or its colour.
 *
 * Null once signed off — the wait is over and an old completedAt must not keep
 * escalating — and null before completion. That decision belongs here rather
 * than inside waitingDaysSince, which cannot tell "not ageing yet" from "aged
 * 0 days" and must return a number for the queue.
 */
export function waitingForSignoff(
  detail: FoliarSessionDetail, now: Date,
): { days: number; tone: WaitingTone } | null {
  const s = detail.session;
  if (s.status !== 'completed' || !s.completedAt) return null;
  const days = waitingDaysSince(s.completedAt, now);
  return { days, tone: waitingTone(days) };
}
